import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomModal from "../../Core/Components/Modal/CustomModal";
import CloseIcon from "../../Common/Svgs/CloseIcon";
import Button from "../../Core/Components/CustomButton";
import { updateRental } from "../../Redux/Slices/rentals.slice";
import { client } from "../../Utils/axiosClient";

const RentalFeeModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const [focus, setFocus] = useState(false);
  const [existing, setExisting] = useState(0);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  const getBorder = () => {
    if (error) return "border-alert-red";
    if (focus) return "border-[#528587]";
    return "border-light-grey";
  };

  const handleCancel = () => {
    setFocus(false);
    setError("");
    setValue("");
    onCancel();
  };

  const handleSave = async () => {
    const valid = /^[0-9]+(\.[0-9]+)?$/.test(String(value).trim());

    if (!valid) {
      setError("Please enter a valid amount to proceed");
      return;
    }

    try {
      dispatch(updateRental({ key: "loading", value: true }));

      const payload = { amount: Number(value || 0) };

      await client.post(
        `${process.env.REACT_APP_API_URL}/rentals/update-rental-fee`,
        payload,
      );

      handleCancel();
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(updateRental({ key: "loading", value: false }));
    }
  };

  const getRentalFee = useCallback(async () => {
    try {
      dispatch(updateRental({ key: "loading", value: true }));

      const response = await client.get(
        `${process.env.REACT_APP_API_URL}/rentals/rental-fee`,
      );

      setExisting(response?.data?.fee || 0);
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(updateRental({ key: "loading", value: false }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (visible) getRentalFee();
  }, [getRentalFee, visible]);

  return (
    <CustomModal visible={visible} onCancel={handleCancel} width="375px">
      <div
        className="p-6"
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="flex items-center justify-between">
          <p className="font-poppins text-base font-medium capitalize">
            Standard Rental Fee
          </p>

          <button type="button" onClick={handleCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        <p className="mt-4 font-aileron text-xs font-normal text-dark-gray pb-4 border-b border-light-grey">
          Update the base daily rental fee for trikes. This fee will apply to
          all new rental bookings going forward.
        </p>

        <p className="mt-4 font-aileron text-xs font-normal text-dark-gray capitalize">
          Current Rental Fee
        </p>

        <div className="mt-1.5 bg-background py-[9px] px-[14px] border border-light-grey rounded-md">
          <p className="font-aileron text-xs font-normal text-dark-gray">
            {existing} KSH
          </p>
        </div>

        <p className="mt-4 mb-1.5 font-aileron text-xs font-normal text-dark-gray capitalize">
          New Rental Fee (KSH/Day)
        </p>

        <input
          className={`border py-[9px] px-[14px] w-full h-8 font-aileron text-xs font-semibold text-black placeholder:text-dark-gray outline-none rounded-md 
            ${getBorder()}`}
          placeholder="Enter Amount"
          value={value}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          onChange={(e) => {
            setValue(e.target.value);
            setError("");
          }}
        />

        {error && (
          <p className="mt-1.5 font-aileron text-xs font-normal text-alert-red">
            {error}
          </p>
        )}

        <div className="mt-4 rounded-md bg-[#FEFAED] p-2 border border-[#F5EDD5]">
          <p className="font-aileron text-xs font-normal text-dark-gray">
            <span className="font-semibold text-black">Note:</span> This update
            will not affect ongoing or previously approved rentals.
          </p>
        </div>

        <div className="mt-4 flex justify-end">
          <div className="flex items-center gap-x-2">
            <Button
              size="filter"
              width="dynamic"
              colors="grey"
              onClick={handleCancel}
            >
              Cancel
            </Button>

            <Button
              size="filter"
              width="dynamic"
              type="submit"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default RentalFeeModal;
